import { z } from '@guestguru/shared';
import { CalendarEventSchema } from '@guestguru/shared';
import { CalendarEvent } from '../types/Planner';
import { apiGet, apiPost, apiPut, apiDelete, planningPath } from './PlanningApiClient';

// API row shape from @guestguru/shared
type ApiCalendarEvent = z.infer<typeof CalendarEventSchema>;

// ── In-memory cache (no localStorage for app data) ──
let calendarEventsCache: CalendarEvent[] | null = null;

function normalizeCalendarEvent(row: ApiCalendarEvent): CalendarEvent {
    return {
        id: String(row.id),
        title: row.title,
        start: row.start_time,
        end: row.end_time,
        category: (row.category ?? 'planning') as CalendarEvent['category'],
        type: row.type ?? '',
        icon: row.icon ?? undefined,
        color: row.color ?? undefined,
        description: row.description ?? undefined,
        location: row.location ?? undefined,
        day: row.day ?? undefined,
    };
}

function toApiPayload(event: Omit<CalendarEvent, 'id'> | CalendarEvent): Record<string, unknown> {
    return {
        title: event.title,
        start_time: event.start,
        end_time: event.end,
        category: event.category ?? null,
        type: event.type ?? null,
        icon: event.icon ?? null,
        color: event.color ?? null,
        description: event.description ?? null,
        location: event.location ?? null,
        day: event.day ?? null,
    };
}

export const loadCalendarEvents = (): CalendarEvent[] => {
    return calendarEventsCache ?? defaultCalendarEvents;
};

export const saveCalendarEvents = (events: CalendarEvent[]): void => {
    calendarEventsCache = events;
};

export const createCalendarEvent = (data: Omit<CalendarEvent, 'id'>): CalendarEvent => {
    const events = loadCalendarEvents();
    const newEvent: CalendarEvent = { ...data, id: `cal-${Date.now()}` };
    saveCalendarEvents([...events, newEvent]);

    apiPost(planningPath('/calendar'), toApiPayload(data)).catch(() => { /* API unreachable */ });

    return newEvent;
};

export const updateCalendarEvent = (id: string, updates: Partial<CalendarEvent>): CalendarEvent | null => {
    const events = loadCalendarEvents();
    const idx = events.findIndex(e => e.id === id);
    if (idx === -1) return null;
    const updated: CalendarEvent = { ...events[idx], ...updates };
    saveCalendarEvents(events.map((e, i) => i === idx ? updated : e));

    apiPut(planningPath(`/calendar/${id}`), toApiPayload(updated)).catch(() => { /* API unreachable */ });

    return updated;
};

/**
 * Creates an event and waits for the API's answer, using the id the API gives.
 * On failure the cache is left as it was and the ApiError (with any field
 * errors) is rethrown, so the form can show it instead of reporting a save that
 * never reached the server.
 */
export const createCalendarEventStrict = async (data: Omit<CalendarEvent, 'id'>): Promise<CalendarEvent> => {
    const created = await apiPost<{ id?: number | string; event?: { id?: number | string } } | null>(
        planningPath('/calendar'), toApiPayload(data));
    const apiId = created?.event?.id ?? created?.id;
    const newEvent: CalendarEvent = { ...data, id: apiId != null ? String(apiId) : `cal-${Date.now()}` };
    saveCalendarEvents([...loadCalendarEvents(), newEvent]);
    return newEvent;
};

/** Updates an event and waits for the API; rolls the cache back and rethrows on failure. */
export const updateCalendarEventStrict = async (id: string, updates: Partial<CalendarEvent>): Promise<CalendarEvent | null> => {
    const before = loadCalendarEvents();
    const idx = before.findIndex(e => e.id === id);
    if (idx === -1) return null;
    const updated: CalendarEvent = { ...before[idx], ...updates };
    saveCalendarEvents(before.map((e, i) => (i === idx ? updated : e)));
    try {
        await apiPut(planningPath(`/calendar/${id}`), toApiPayload(updated));
    } catch (err) {
        saveCalendarEvents(before);
        throw err;
    }
    return updated;
};

export const archiveCalendarEvent = (id: string): CalendarEvent | null =>
    updateCalendarEvent(id, { archived: true });

export const restoreCalendarEvent = (id: string): CalendarEvent | null =>
    updateCalendarEvent(id, { archived: false });

export const deleteCalendarEvent = (id: string): boolean => {
    const events = loadCalendarEvents();
    const target = events.find(e => e.id === id);
    if (!target || !target.archived) return false;
    saveCalendarEvents(events.filter(e => e.id !== id));

    apiDelete(planningPath(`/calendar/${id}`)).catch(() => { /* API unreachable */ });

    return true;
};

export const refreshCalendarFromApi = async (): Promise<CalendarEvent[]> => {
    try {
        const raw = await apiGet<ApiCalendarEvent[] | { events: ApiCalendarEvent[] }>(planningPath('/calendar'));
        const apiEvents = Array.isArray(raw) ? raw : (raw as { events: ApiCalendarEvent[] })?.events;
        if (Array.isArray(apiEvents) && apiEvents.length > 0) {
            const normalized = apiEvents.map(normalizeCalendarEvent);
            saveCalendarEvents(normalized);
            return normalized;
        }
    } catch {
        // API unreachable — keep existing in-memory cache
    }
    return loadCalendarEvents();
};

export const syncCalendarToApi = async (): Promise<void> => {
    const events = loadCalendarEvents();
    await Promise.allSettled(
        events.map(event => apiPost(planningPath('/calendar'), toApiPayload(event)))
    );
};

const defaultCalendarEvents: CalendarEvent[] = [
    {
        id: 'e1',
        title: 'Strategy Meeting',
        start: '2026-04-06T09:00:00Z',
        end: '2026-04-06T10:30:00Z',
        category: 'planning',
        type: 'planning',
        icon: 'S',
        color: '#f0edff',
    },
    {
        id: 'e2',
        title: 'Brand Review',
        start: '2026-04-06T11:00:00Z',
        end: '2026-04-06T12:00:00Z',
        category: 'official',
        type: 'official',
        icon: 'B',
        color: '#fdf2ff',
    },
];
